import { Fragment, useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import { groupResults, resultHref, shouldSearch, useDebounced, useSearch, useViewer } from '@/lib/search';

/** True when the keystroke is going into a field, so "/" types a slash there instead of jumping to search. */
const typingInField = (t: EventTarget | null) =>
  t instanceof HTMLElement && (t.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(t.tagName));

/**
 * The search box in the header. Results come from the database as the signed-in
 * person, so they only ever include what that person could open anyway.
 * "/" or Ctrl/Cmd+K jumps to it; arrows move, Enter opens, Escape closes.
 */
export function GlobalSearch() {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const boxRef = useRef<HTMLFormElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const viewer = useViewer();
  const dq = useDebounced(q, 150);
  const results = useSearch(dq);
  const groups = useMemo(() => groupResults(results.data ?? [], viewer.isStaff), [results.data, viewer.isStaff]);
  const flat = useMemo(() => groups.flatMap(g => g.items), [groups]);
  const seeAll = `/search?q=${encodeURIComponent(q.trim())}`;
  const showing = open && shouldSearch(q);
  const count = flat.length + 1;   // every result, then "See all results"

  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      const slash = e.key === '/' && !e.metaKey && !e.ctrlKey && !e.altKey && !typingInField(e.target);
      const cmdK = (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k';
      if (slash || cmdK) { e.preventDefault(); inputRef.current?.focus(); inputRef.current?.select(); setOpen(true); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);
  useEffect(() => {
    const onDown = (e: MouseEvent) => { if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);
  useEffect(() => { setOpen(false); setActive(-1); }, [location.pathname, location.search, location.hash]);
  useEffect(() => setActive(-1), [dq]);

  const go = (to: string) => { setOpen(false); setActive(-1); inputRef.current?.blur(); navigate(to); };

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      if (!showing) { setOpen(true); return; }
      e.preventDefault();
      setActive(a => e.key === 'ArrowDown' ? (a + 1) % count : (a <= 0 ? count - 1 : a - 1));
    } else if (e.key === 'Escape') {
      if (q) setQ(''); else inputRef.current?.blur();
      setOpen(false);
    } else if (e.key === 'Tab') {
      setOpen(false);
    }
  }

  let i = -1;   // running option index across groups
  return (
    <form ref={boxRef} role="search" className="relative order-last w-full sm:order-none sm:w-64 md:w-80"
      onSubmit={e => { e.preventDefault(); if (!shouldSearch(q)) return; go(active >= 0 && active < flat.length ? resultHref(flat[active]!, viewer) : seeAll); }}>
      <label className="relative block">
        <span className="sr-only">Search</span>
        <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
        <input ref={inputRef} className="input py-2 pl-9 pr-9 text-[15px]" placeholder="Search" autoComplete="off" spellCheck={false}
          role="combobox" aria-autocomplete="list" aria-expanded={showing} aria-controls="global-search-results"
          aria-activedescendant={showing && active >= 0 ? `gs-${active}` : undefined}
          value={q} onChange={e => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)} onKeyDown={onKeyDown} />
        <kbd className="pointer-events-none absolute right-2.5 top-1/2 hidden -translate-y-1/2 rounded-sm border border-stone-200 px-1.5 text-xs text-ink-500 sm:block">/</kbd>
      </label>
      {showing && (
        <div className="card absolute left-0 right-0 top-full z-30 mt-1 max-h-[70vh] overflow-y-auto p-0 shadow-lg sm:left-auto sm:w-[26rem]">
          <ul id="global-search-results" role="listbox" aria-label="Search results" className="py-1">
            {results.isLoading && <li className="px-4 py-3 text-sm text-ink-500" role="status">Searching…</li>}
            {results.isError && <li className="px-4 py-3 text-sm text-brand-700" role="alert">Search isn't working just now. Please try again.</li>}
            {results.data && flat.length === 0 && !results.isFetching && (
              <li className="px-4 py-3 text-sm text-ink-500">
                Nothing found for “{q.trim()}”.
                {!viewer.isStaff && <span className="mt-1 block">You can find your own household, events and dojos{viewer.instructorDojoIds.length > 0 && ', and your students'}.</span>}
              </li>
            )}
            {groups.map(g => (
              <Fragment key={g.key}>
                <li role="presentation" className="px-4 pb-1 pt-3 text-xs font-semibold uppercase tracking-wide text-ink-500">{g.label}</li>
                {g.items.map(r => {
                  i += 1;
                  const me = i;
                  return (
                    <li key={`${r.kind}-${r.id}`} id={`gs-${me}`} role="option" aria-selected={me === active}>
                      <Link to={resultHref(r, viewer)} onClick={() => { setOpen(false); setActive(-1); }} onMouseEnter={() => setActive(me)}
                        className={`block border-l-2 px-4 py-2 ${me === active ? 'border-brand-600 bg-stone-100' : 'border-transparent'}`}>
                        <span className="block font-semibold text-ink-900">{r.title}</span>
                        {r.subtitle && <span className="block text-sm text-ink-500">{r.subtitle}</span>}
                      </Link>
                    </li>
                  );
                })}
              </Fragment>
            ))}
            {(flat.length > 0 || results.isFetching) && (
              <li id={`gs-${flat.length}`} role="option" aria-selected={active === flat.length} className="mt-1 border-t border-stone-200">
                <Link to={seeAll} onClick={() => setOpen(false)} onMouseEnter={() => setActive(flat.length)}
                  className={`block px-4 py-2.5 text-sm font-semibold text-indigo-600 ${active === flat.length ? 'bg-stone-100' : ''}`}>
                  See all results
                </Link>
              </li>
            )}
          </ul>
        </div>
      )}
    </form>
  );
}
